import { Injectable } from '@angular/core';
import {
  IResourceMethodObservable,
  IResourceMethodPromiseFull,
  Resource,
  ResourceAction,
  ResourceGlobalConfig,
  ResourceHandler,
  ResourceParams,
  ResourceRequestMethod,
  ResourceResponseBodyType,
} from '@ngx-resource/core';
import { ProcessTypeEnum } from '../models/cartDetails/process-type.enum';
import { INipBlacklisted } from '../interfaces/nip-blacklisted.interface';
import { IOrderSummary } from '../interfaces/order-summary.interface';

ResourceGlobalConfig.headers = { 'Content-Type': 'application/json;charset=utf-8' };

export interface IUser {
  bundleNo?: number;
  uid?: string;
}

export interface IMnp {
  changeMarker: string;
  mnpDate?: string;
  mnpOperator?: string;
  mnpOperatorId?: string;
  mnpServiceType?: string;
  msisdn?: string;
  bundleNo?: IUser;
}

export interface IOtp {
  msisdn: number;
  processType: ProcessTypeEnum;
  bundleNo: IUser;
}

export interface ICcds {
  mainMsisdnColumnName: string;
  source: string;
  url: string;
}

export interface IChangeDevice {
  offerId: string;
  bundleNo: IUser;
}


export interface IAccessory {
  accessoryId: string;
  b2c: boolean;
  delta: number;
  bundleNo: IUser;
}

export interface IGadget {
  deviceId: string;
  offerId: number;
  bundleNo: IUser;
}

export interface IAddons {
  addonsToAdd?: [];
  addonsToDelete?: [];
  addonCodes?: [];
  bundleNo: IUser;
}

export interface IMsisdn {
  phone: string;
  bundleNo?: IUser;
  msisdn?: number;
}

export interface IDelivery {
  code: string;
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/cart/multi',
})
export class MultiSaveResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/save/secondStep',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    },
    asResourceResponse: true
  })
  saveSecondStep: IResourceMethodObservable<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/submit/secondStep',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    },
    asResourceResponse: true
  })
  submitSecondStep: IResourceMethodPromiseFull<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/save/thirdStep',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    }
  })
  saveThirdStep: IResourceMethodObservable<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/submit/thirdStep',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    },
    asResourceResponse: true
  })
  submitThirdStep: IResourceMethodPromiseFull<void, void>;
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/ccds',
})
export class CcdsResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/contactWithClient'
  })
  contactWithClient: IResourceMethodObservable<ICcds, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/mnp'
})
export class MnpResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/save'
  })
  save: IResourceMethodObservable<IMnp, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/operatorList/{!changeMarker}'
  })
  operatorList: IResourceMethodObservable<IMnp, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/otp'
})
export class OtpResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/send/{!bundleNo}/{!msisdn}/{!processType}',
    responseBodyType: ResourceResponseBodyType.Text
  })
  send: IResourceMethodObservable<IOtp, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/verify/{!bundleNo}/{!msisdn}/{!authCode}'
  })
  verify: IResourceMethodObservable<IOtp, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/cart'
})
export class NewCartResources extends Resource {
  @ResourceAction({
    method: ResourceRequestMethod.Get
  })
  cart: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/config'
  })
  config: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/documents'
  })
  documents: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/checkCompleteAgreements'
  })
  checkAgreements: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/changeDevice/'
  })
  changeDevice: IResourceMethodObservable<IChangeDevice, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/changeProcess/{!bundleNo}/{!processType}'
  })
  changeProcess: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Delete,
    path: '/messageBundles'
  })
  message: IResourceMethodObservable<IUser, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/optionalMember'
  })
  addOptionalMember: IResourceMethodObservable<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/removeBundle/{!bundleNo}'
  })
  removeOptionalMember: IResourceMethodObservable<IUser, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Delete,
    path: '/remove'
  })
  removeCart: IResourceMethodObservable<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/contractDetails'
  })
  contractDetails: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/bundleItemsDetails/{!productId}'
  })
  bundleItemsDetails: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/switch/{!bundleNo}'
  })
  switch: IResourceMethodObservable<IUser, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/internetDetails'
  })
  internetDetails: IResourceMethodObservable<{}, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/validate',
    asResourceResponse: true
  })
  validateCart: IResourceMethodPromiseFull<void, void>;
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2'
})
export class CheckoutResources extends Resource {
  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/checkout/isNipBlacklisted/{!nip}'
  })
  nip: IResourceMethodObservable<{ nip: string }, INipBlacklisted>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/order/summary/{!cartId}'
  })
  summary: IResourceMethodObservable<{ cartId: string }, IOrderSummary.Root>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/checkout/paymentAndDelivery'
  })
  paymentAndDelivery: IResourceMethodObservable<{}, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/cart'
})
export class AccessoryResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/accessory/quantity'
  })
  quantity: IResourceMethodObservable<IAccessory, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/removeEntry/{!bundleNo}/{!offerId}/{!deviceId}'
  })
  removeEntry: IResourceMethodObservable<void, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/cart'
})
export class GadgetResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/removeGadget/{!bundleNo}/{!offerId}/{!sku}'
  })
  removeGadget: IResourceMethodObservable<IGadget, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/cart/addons'
})
export class AddonsResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/update'
  })
  update: IResourceMethodObservable<IAddons, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Delete,
    path: ''
  })
  delete: IResourceMethodObservable<IAddons, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/msisdn'
})
export class MsisdnResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/tv/{!phone}'
  })
  saveTvMsisdn: IResourceMethodObservable<IMsisdn, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/{!bundleNo}',
  })
  msisdn: IResourceMethodObservable<IMsisdn, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/selectMsisdn/{!bundleNo}/{!msisdn}'
  })
  selectMsisdn: IResourceMethodObservable<IMsisdn, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/user'
})
export class NewCustomerResources extends Resource {
  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/customerData'
  })
  customerData: IResourceMethodObservable<{}, void>;


  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/logout'
  })
  logout: IResourceMethodObservable<void, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2/pos'
})
export class PosResources extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/search',
    withCredentials: true
  })
  searchPos: IResourceMethodObservable<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/findInArea',
    withCredentials: true
  })
  findPosInArea: IResourceMethodObservable<{}, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/p2api/v2'
})
export class AgreementsResource extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/agreements',
  })
  get: IResourceMethodObservable<void, void>;

  @ResourceAction({
    method: ResourceRequestMethod.Post,
    path: '/agreements/update',
  })
  update: IResourceMethodObservable<{}, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}

@Injectable({
  providedIn: 'root'
})
@ResourceParams({
  pathPrefix: '/rest'
})
export class RestOrderResource extends Resource {

  @ResourceAction({
    method: ResourceRequestMethod.Get,
    path: '/orderProgressInfo',
    withCredentials: true,
    asResourceResponse: true
  })
  orderProgressInfo: IResourceMethodPromiseFull<void, void>;

  constructor(handler: ResourceHandler) {
    super(handler);
  }
}
